import React, { useState } from 'react';
import { 
  Grid, 
  TextField, 
  Typography, 
  Button,
  CircularProgress,
  Box,
  Alert,
  IconButton,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import { 
  Settings,
  Upload,
  Close,
  Description,
  Image as ImageIcon,
  Lightbulb,
  VolumeUp,
  Straighten,
  Add,
  Delete
} from '@mui/icons-material';
import { BandRecommendationFormData } from '../../types/BandRecommendations';
import axios from 'axios';

interface TechnicalRequirementsProps {
  formData: BandRecommendationFormData;
  setFormData: React.Dispatch<React.SetStateAction<BandRecommendationFormData>>;
}

interface FileUploadState {
  stagePlot: { uploading: boolean; error: string | null };
  techSpecs: { uploading: boolean; error: string | null };
  lightingSpec: { uploading: boolean; error: string | null };
  stageImage: { uploading: boolean; error: string | null };
}

const TechnicalRequirements: React.FC<TechnicalRequirementsProps> = ({ formData, setFormData }) => {
  const [uploadState, setUploadState] = useState<FileUploadState>({
    stagePlot: { uploading: false, error: null },
    techSpecs: { uploading: false, error: null },
    lightingSpec: { uploading: false, error: null },
    stageImage: { uploading: false, error: null }
  });

  const [newEquipment, setNewEquipment] = useState('');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      technicalRider: {
        ...(prevData.technicalRider || {}),
        [name]: value,
      },
    }));
  };

  const addEquipment = () => {
    if (newEquipment.trim()) {
      const currentEquipment = formData.technicalRider?.additionalEquipment || [];
      setFormData(prevData => ({
        ...prevData,
        technicalRider: {
          ...prevData.technicalRider,
          additionalEquipment: [...currentEquipment, newEquipment.trim()]
        }
      }));
      setNewEquipment('');
    }
  };

  const removeEquipment = (index: number) => {
    const currentEquipment = formData.technicalRider?.additionalEquipment || [];
    setFormData(prevData => ({
      ...prevData,
      technicalRider: {
        ...prevData.technicalRider,
        additionalEquipment: currentEquipment.filter((_, i) => i !== index)
      }
    }));
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fileType: 'stagePlot' | 'techSpecs' | 'lightingSpec' | 'stageImage'
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Update uploading state
    setUploadState(prev => ({
      ...prev,
      [fileType]: { ...prev[fileType], uploading: true, error: null }
    }));

    // Validate file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      setUploadState(prev => ({
        ...prev,
        [fileType]: { uploading: false, error: 'File size exceeds 50MB limit' }
      }));
      return;
    }

    // Validate file type
    const validTypes = fileType === 'stageImage' 
      ? ['image/jpeg', 'image/png', 'image/gif']
      : ['application/pdf'];
    
    if (!validTypes.includes(file.type)) {
      setUploadState(prev => ({
        ...prev,
        [fileType]: { 
          uploading: false, 
          error: fileType === 'stageImage' 
            ? 'Please upload a JPEG, PNG, or GIF file'
            : 'Please upload a PDF file'
        }
      }));
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', fileType);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/upload/technical`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          withCredentials: true,
        }
      );

      setFormData(prevData => ({
        ...prevData,
        technicalRider: {
          ...prevData.technicalRider,
          [fileType]: response.data.fileUrl,
        },
      }));

      setUploadState(prev => ({
        ...prev,
        [fileType]: { uploading: false, error: null }
      }));
    } catch (error: any) {
      console.error(`Error uploading ${fileType}:`, error);
      setUploadState(prev => ({
        ...prev,
        [fileType]: { 
          uploading: false, 
          error: error.response?.data?.message || 'Upload failed. Please try again.'
        }
      }));
    }
  };

  const handleRemoveFile = (fileType: 'stagePlot' | 'techSpecs' | 'lightingSpec' | 'stageImage') => {
    setFormData(prevData => ({
      ...prevData,
      technicalRider: {
        ...prevData.technicalRider,
        [fileType]: '',
      },
    }));
  };

  const renderFileUploadSection = (
    fileType: 'stagePlot' | 'techSpecs' | 'lightingSpec' | 'stageImage',
    label: string,
    description: string,
    icon: React.ReactNode
  ) => {
    const { uploading, error } = uploadState[fileType];
    const currentFile = formData.technicalRider?.[fileType];

    return (
      <Grid item xs={12} sm={6}>
        <Box sx={{ 
          p: 2, 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {icon}
            <Typography variant="subtitle2">{label}</Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
            {description}
          </Typography>

          {currentFile ? (
            <Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                p: 1, 
                bgcolor: 'success.light', 
                color: 'success.contrastText',
                borderRadius: 1,
                mb: 1
              }}>
                {fileType === 'stageImage' ? <ImageIcon fontSize="small" /> : <Description fontSize="small" />}
                <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {currentFile.split('/').pop()}
                </Typography>
                <IconButton onClick={() => handleRemoveFile(fileType)} size="small" sx={{ color: 'inherit' }}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
              <Button
                variant="outlined"
                component="label"
                size="small"
                fullWidth
              >
                Replace File
                <input
                  type="file"
                  hidden
                  accept={fileType === 'stageImage' ? "image/*" : ".pdf"}
                  onChange={(e) => handleFileUpload(e, fileType)}
                />
              </Button>
            </Box>
          ) : (
            <Button
              variant="outlined"
              component="label"
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={16} /> : <Upload />}
              fullWidth
            >
              {uploading ? 'Uploading...' : 'Upload File'}
              <input
                type="file"
                hidden
                accept={fileType === 'stageImage' ? "image/*" : ".pdf"}
                onChange={(e) => handleFileUpload(e, fileType)}
              />
            </Button>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </Box>
      </Grid>
    );
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {/* File Uploads - Most Important */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Settings color="primary" />
            Technical Documents & Images
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Upload your technical documents and stage images - these are essential for venues to prepare properly.
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {renderFileUploadSection(
              'stagePlot',
              'Stage Plot',
              'Upload a PDF showing your stage layout and equipment placement.',
              <Description color="primary" />
            )}
            
            {renderFileUploadSection(
              'techSpecs',
              'Technical Specifications',
              'Upload a detailed PDF with all your technical requirements.',
              <Settings color="primary" />
            )}
            
            {renderFileUploadSection(
              'lightingSpec',
              'Lighting Specification',
              'Upload specific lighting requirements if you have complex needs.',
              <Lightbulb color="primary" />
            )}
            
            {renderFileUploadSection(
              'stageImage',
              'Stage Image',
              'Upload a photo showing your typical stage setup.',
              <ImageIcon color="primary" />
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Additional Equipment */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Additional Equipment
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            List any special equipment you need that isn't covered above.
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Add equipment item"
                value={newEquipment}
                onChange={(e) => setNewEquipment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addEquipment()}
                placeholder="e.g., Piano stool, music stands"
                size="small"
                fullWidth
                inputProps={{ autoComplete: 'off' }}
              />
              <Button
                variant="contained"
                onClick={addEquipment}
                disabled={!newEquipment.trim()}
                sx={{ minWidth: 'auto' }}
              >
                <Add />
              </Button>
            </Box>

            {formData.technicalRider?.additionalEquipment && formData.technicalRider.additionalEquipment.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {formData.technicalRider.additionalEquipment.map((item, index) => (
                  <Chip
                    key={index}
                    label={item}
                    onDelete={() => removeEquipment(index)}
                    deleteIcon={<Delete />}
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No additional equipment specified
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Basic Technical Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Basic Technical Requirements
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Provide essential technical details about your performance requirements.
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="stageSize"
                label="Stage Size Requirements"
                value={formData.technicalRider?.stageSize ?? ''}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                placeholder="e.g., 6m x 4m minimum"
                InputProps={{
                  startAdornment: <Straighten sx={{ color: 'action.active', mr: 1 }} />
                }}
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="audioRequirements"
                label="Audio Requirements"
                value={formData.technicalRider?.audioRequirements ?? ''}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                placeholder="e.g., Full PA system, 8 input channels"
                InputProps={{
                  startAdornment: <VolumeUp sx={{ color: 'action.active', mr: 1 }} />
                }}
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="lightingRequirements"
                label="Lighting Requirements"
                value={formData.technicalRider?.lightingRequirements ?? ''}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                placeholder="e.g., Basic stage wash, no special effects needed"
                InputProps={{
                  startAdornment: <Lightbulb sx={{ color: 'action.active', mr: 1, alignSelf: 'flex-start', mt: 1 }} />
                }}
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TechnicalRequirements;